<template>
  <!-- Main Container with modern gradient background -->
  <div class="min-h-screen bg-white dark:bg-gradient-to-br dark:from-[#0c1427] dark:to-[#1a2332]">
    <!-- Header Section with glass morphism -->
    <div class="px-4 sm:px-6 lg:px-8 pt-6 pb-4">
      <div class="bg-white dark:bg-white/10 backdrop-blur-[20px] shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl p-6 border border-gray-100 dark:border-white/10">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 class="text-2xl font-bold bg-gradient-to-r from-[#007AFF] to-[#5856D6] bg-clip-text text-transparent">
              {{ directT('modules.members.title') }}
            </h3>
            <p class="text-sm text-[#8e8e93] dark:text-gray-400 mt-1">{{ directT('modules.members.subTitle') }}</p>
        </div>
          <div class="flex items-center gap-3">
            <button 
              @click="exportToExcel"
              class="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#34C759] to-[#32D74B] text-white font-medium text-sm shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              <i class="material-symbols-outlined !text-[20px]">cloud_download</i>
                {{ directT('modules.members.exportToExcel') }}
            </button>
            
            <button 
              @click="showAddMemberModal"
              class="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white font-medium text-sm shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              <i class="material-symbols-outlined !text-[20px]">person_add</i>
                {{ directT('modules.members.addMember') }}
            </button>
          </div>
        </div>
        </div>
    </div>

    <!-- Search Section -->
    <div class="px-4 sm:px-6 lg:px-8 pb-4">
      <div class="bg-white dark:bg-white/10 backdrop-blur-[20px] shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl p-6 border border-gray-100 dark:border-white/10">
        <div class="relative">
          <label class="leading-none absolute ltr:left-[13px] rtl:right-[13px] text-[#8e8e93] dark:text-gray-400 mt-px top-1/2 -translate-y-1/2">
            <i class="material-symbols-outlined !text-[20px]">search</i>
          </label>
          <input 
            v-model="searchQuery" 
            type="text" 
            :placeholder="directT('modules.members.searchMember')" 
            class="w-full px-4 py-3 ltr:pl-[38px] rtl:pr-[38px] bg-white dark:bg-white/5 shadow-[0_2px_8px_0_rgba(0,0,0,0.08)] border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white placeholder-[#8e8e93] dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent focus:shadow-[0_4px_12px_0_rgba(0,122,255,0.15)] transition-all"
          >
        </div>
        </div>
        </div>

    <!-- Members Table with modern design -->
    <div class="px-4 sm:px-6 lg:px-8 pb-8">
      <div class="bg-white dark:bg-white/10 backdrop-blur-[20px] shadow-[0_20px_40px_0_rgba(0,0,0,0.1)] rounded-3xl overflow-hidden border border-gray-100 dark:border-white/10">
        <div v-if="loading" class="flex justify-center items-center py-20">
          <div class="relative">
            <div class="w-16 h-16 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
            <div class="w-16 h-16 rounded-full border-4 border-t-[#007AFF] animate-spin absolute top-0"></div>
          </div>
        </div>
        
        <div v-else-if="error" class="p-8 text-center">
          <div class="w-20 h-20 rounded-full bg-[#FF3B30]/10 flex items-center justify-center mx-auto mb-4">
            <i class="material-symbols-outlined !text-[40px] text-[#FF3B30]">error</i>
          </div>
          <p class="text-[#FF3B30] font-medium">{{ error }}</p>
        </div>
        
        <div v-else-if="filteredMembers.length === 0" class="text-center py-20">
          <div class="w-20 h-20 rounded-full bg-[#f2f2f7] dark:bg-white/5 flex items-center justify-center mx-auto mb-4">
            <i class="material-symbols-outlined !text-[40px] text-[#8e8e93] dark:text-gray-400">group_off</i>
          </div>
          <p class="text-base font-medium text-[#1d1d1f] dark:text-white">{{ directT('modules.members.noSuperAdminFound') }}</p>
          <p class="text-sm mt-1 text-[#8e8e93] dark:text-gray-400">{{ directT('modules.members.noMembersFound') }}</p>
        </div>
        
        <div v-else class="overflow-x-auto">
            <table class="w-full">
            <thead>
              <tr style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
                <th class="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.id') }}
                        </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.member') }}
                        </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.email') }}
                        </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.phone') }}
                        </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.location') }}
                        </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.joiningDate') }}
                        </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.lastActive') }}
                        </th>
                <th class="px-6 py-4 text-center text-xs font-semibold text-white uppercase tracking-wider">
                            {{ directT('modules.members.action') }}
                        </th>
                    </tr>
                </thead>
                
            <tbody class="divide-y divide-gray-100 dark:divide-white/10">
              <tr v-for="member in paginatedMembers" :key="member.id" class="hover:bg-[#f2f2f7] dark:hover:bg-white/5 transition-colors">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-[#8e8e93] dark:text-gray-400">
                  <span class="font-mono">#{{ member.id.substring(0, 8).toUpperCase() }}</span>
                        </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center">
                      <img src="@/assets/images/users/user6.jpg" class="w-full h-full object-cover" alt="user-image">
                                </div>
                    <div>
                      <div class="text-sm font-medium text-[#1d1d1f] dark:text-white">{{ member.name }}</div>
                      <div class="text-xs text-[#8e8e93] dark:text-gray-400">{{ getRoleName(member.role) }}</div>
                                </div>
                            </div>
                        </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-[#3c3c43] dark:text-gray-300">
                            {{ member.email }}
                        </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-[#3c3c43] dark:text-gray-300">
                            {{ member.phone }}
                        </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-[#3c3c43] dark:text-gray-300">
                            {{ member.location }}
                        </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-[#3c3c43] dark:text-gray-300">
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-[#34C759] to-[#32D74B] flex items-center justify-center">
                      <i class="material-symbols-outlined text-white !text-[16px]">calendar_today</i>
                    </div>
                            {{ member.joiningDate }}
                  </div>
                        </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-[#3c3c43] dark:text-gray-300">
                  <span class="px-3 py-1 rounded-full bg-[#007AFF]/20 text-[#007AFF] font-medium">
                            {{ member.lastActive }}
                  </span>
                        </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="flex items-center justify-center gap-2">
                    <button 
                      type="button" 
                      @click="viewMember(member)" 
                      class="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center transition-all hover:scale-110 hover:shadow-lg"
                    >
                      <i class="material-symbols-outlined !text-[18px]">visibility</i>
                                </button>
                    <button 
                      type="button" 
                      @click="showEditMember(member)" 
                      class="w-8 h-8 rounded-full bg-[#5856D6] text-white flex items-center justify-center transition-all hover:scale-110 hover:shadow-lg"
                    >
                      <i class="material-symbols-outlined !text-[18px]">edit</i>
                                </button>
                    <button 
                      type="button" 
                      @click="confirmDelete(member)" 
                      class="w-8 h-8 rounded-full bg-[#FF3B30] text-white flex items-center justify-center transition-all hover:scale-110 hover:shadow-lg"
                    >
                      <i class="material-symbols-outlined !text-[18px]">delete</i>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
                                </div>

        <Pagination 
          :items="itemsPerPage" 
          :total="filteredMembers.length" 
          :current-page="currentPage"
          @change-page="changePage"
          class="px-6 py-4 border-t border-gray-100 dark:border-white/10" 
        />
      </div>
                                </div>
                            </div>

  <!-- Edit Member Modal with modern design -->
  <div v-if="showEditModal" class="fixed inset-0 flex items-center justify-center z-50 px-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="cancelEdit"></div>
    <div class="relative transform transition-all duration-300 scale-100 bg-white dark:bg-gray-900/95 backdrop-blur-[20px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto mt-[100px] sm:mt-0">
      
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-2xl font-semibold text-[#1d1d1f] dark:text-white">{{ directT('modules.members.editMember') }}</h3>
        <button @click="cancelEdit" class="w-10 h-10 rounded-full bg-[#f2f2f7] dark:bg-white/10 flex items-center justify-center hover:bg-[#e5e5ea] dark:hover:bg-white/20 transition-colors">
          <i class="material-symbols-outlined text-[#3c3c43] dark:text-gray-300">close</i>
                                </button>
        </div>

      <div v-if="editError" class="mb-4 p-4 bg-[#FFE5E5] dark:bg-red-500/20 rounded-2xl">
        <p class="text-[#FF3B30] font-medium">{{ editError }}</p>
        </div>

        <form @submit.prevent="saveEdit">
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.name') }}</label>
            <input 
              v-model="editingMember.name" 
              type="text" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" 
              required
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.phone') }}</label>
            <input 
              v-model="editingMember.phone" 
              type="text" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.location') }}</label>
            <input 
              v-model="editingMember.location" 
              type="text" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.role') }}</label>
            <select 
              v-model="editingMember.role" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
            >
              <option value="superadmin">{{ directT('modules.members.superadmin') || 'Super Admin' }}</option>
              <option value="admin">{{ directT('modules.members.admin') }}</option>
              <option value="editor">{{ directT('modules.members.editor') }}</option>
              <option value="member">{{ directT('modules.members.member') }}</option>
              <option value="viewer">{{ directT('modules.members.viewer') }}</option>
            </select>
          </div>
          
          <!-- Screen Permissions -->
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.screenPermissions') }}</label>
            
            <div v-if="loadingScreens" class="text-center p-4">
              <div class="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p class="text-[#8e8e93] dark:text-gray-400 mt-2">{{ directT('modules.members.loadingScreens') }}</p>
            </div>
            
            <div v-else-if="screenError" class="p-4 bg-[#FFE5E5] dark:bg-red-500/20 rounded-xl">
              <p class="text-[#FF3B30]">{{ screenError }}</p>
            </div>
            
            <div v-else class="max-h-60 overflow-y-auto border border-[#e5e5ea] dark:border-white/10 rounded-xl p-4 bg-white dark:bg-white/5">
              <div class="grid grid-cols-2 gap-3">
                <div v-for="screen in screens" :key="screen.screen_key" class="flex items-center gap-2 p-2 hover:bg-[#f2f2f7] dark:hover:bg-white/5 rounded-lg transition-colors">
                  <input 
                    type="checkbox" 
                    :id="`screen_${screen.screen_key}`" 
                    v-model="screenPermissions[screen.screen_key]" 
                    class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                  />
                  <label :for="`screen_${screen.screen_key}`" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                    {{ screen.display_name }}
                  </label>
                </div>
              </div>
            </div>
            
            <div class="mt-3 flex justify-between">
              <button 
                type="button"
                @click="selectAllScreens(true)"
                class="text-xs text-[#007AFF] hover:underline font-medium"
              >
                {{ directT('modules.members.selectAll') }}
                                </button>
              <button 
                type="button"
                @click="selectAllScreens(false)"
                class="text-xs text-[#8e8e93] hover:text-[#3c3c43] dark:text-gray-400 dark:hover:text-gray-300"
              >
                {{ directT('modules.members.deselectAll') }}
                                </button>
                            </div>
                                </div>
          
          <!-- Additional Permissions -->
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.additionalPermissions') }}</label>
            
            <div class="space-y-3 border border-[#e5e5ea] dark:border-white/10 rounded-xl p-4 bg-white dark:bg-white/5">
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="is_owner" 
                  v-model="editingMember.permissions.is_owner" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="is_owner" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.organizationOwner') }}
                </label>
                                </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="edit_all_leads" 
                  v-model="editingMember.permissions.edit_all_leads" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="edit_all_leads" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.editAllLeads') }}
                </label>
                            </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="view_all_leads" 
                  v-model="editingMember.permissions.view_all_leads" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="view_all_leads" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.viewAllLeads') }}
                </label>
              </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="can_invite_users" 
                  v-model="editingMember.permissions.can_invite_users" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="can_invite_users" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.canInviteUsers') }}
                </label>
              </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="view_all_calendars" 
                  v-model="editingMember.permissions.view_all_calendars" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="view_all_calendars" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.viewAllCalendars') }}
                </label>
              </div>
              </div>
            </div>
          </div>
          
        <div class="flex items-center justify-end gap-3 mt-8">
            <button 
              type="button" 
              @click="cancelEdit" 
            class="px-6 py-3 rounded-2xl font-medium transition-all bg-[#f2f2f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white hover:bg-[#e5e5ea] dark:hover:bg-white/20"
            >
              {{ directT('modules.members.cancel') }}
                                </button>
            <button 
              type="submit" 
            class="px-6 py-3 rounded-2xl font-medium transition-all bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white shadow-md hover:shadow-lg transform hover:scale-105"
              :disabled="saving"
            >
            <span v-if="saving" class="flex items-center gap-2">
              <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {{ directT('modules.members.saving') }}
            </span>
              <span v-else>{{ directT('modules.members.save') }}</span>
                                </button>
                            </div>
        </form>
                                </div>
                            </div>

  <!-- View Member Modal with modern design -->
  <div v-if="showViewModal" class="fixed inset-0 flex items-center justify-center z-50 px-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="showViewModal = false"></div>
    <div class="relative transform transition-all duration-300 scale-100 bg-white dark:bg-gray-900/95 backdrop-blur-[20px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto mt-[100px] sm:mt-0">
      
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-2xl font-semibold text-[#1d1d1f] dark:text-white">{{ directT('modules.members.memberDetails') }}</h3>
        <button @click="showViewModal = false" class="w-10 h-10 rounded-full bg-[#f2f2f7] dark:bg-white/10 flex items-center justify-center hover:bg-[#e5e5ea] dark:hover:bg-white/20 transition-colors">
          <i class="material-symbols-outlined text-[#3c3c43] dark:text-gray-300">close</i>
                                </button>
                            </div>
        
      <!-- Profile Section -->
      <div class="flex items-center gap-5 mb-8">
        <div class="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center shadow-lg">
            <img src="@/assets/images/users/user6.jpg" class="w-full h-full object-cover" alt="">
                                </div>
        <div>
          <h4 class="text-xl font-semibold text-[#1d1d1f] dark:text-white">{{ viewingMember.name }}</h4>
          <p class="text-[#8e8e93] dark:text-gray-400">{{ viewingMember.email }}</p>
          <span class="inline-block px-3 py-1 mt-2 text-xs font-medium rounded-full" 
              :class="{
              'bg-gradient-to-r from-[#34C759] to-[#32D74B] text-white': viewingMember.role === 'admin',
              'bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white': viewingMember.role === 'editor',
              'bg-gradient-to-r from-[#AF52DE] to-[#5E5CE6] text-white': viewingMember.role === 'member',
              'bg-[#8e8e93]/20 text-[#8e8e93] dark:text-gray-400': viewingMember.role === 'viewer'
              }">
              {{ getRoleName(viewingMember.role) }}
                                    </span>
                                </div>
                            </div>
        
      <!-- Details Section -->
        <div class="space-y-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center shadow-md">
            <i class="material-symbols-outlined text-white !text-[20px]">call</i>
                            </div>
            <div>
            <div class="text-xs text-[#8e8e93] dark:text-gray-400">{{ directT('modules.members.phone') }}</div>
            <div class="text-sm font-medium text-[#1d1d1f] dark:text-white">{{ viewingMember.phone }}</div>
            </div>
          </div>
          
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF9500] to-[#FF6200] flex items-center justify-center shadow-md">
            <i class="material-symbols-outlined text-white !text-[20px]">location_on</i>
                                </div>
            <div>
            <div class="text-xs text-[#8e8e93] dark:text-gray-400">{{ directT('modules.members.location') }}</div>
            <div class="text-sm font-medium text-[#1d1d1f] dark:text-white">{{ viewingMember.location }}</div>
                                </div>
                            </div>
          
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#34C759] to-[#32D74B] flex items-center justify-center shadow-md">
            <i class="material-symbols-outlined text-white !text-[20px]">event</i>
                            </div>
            <div>
            <div class="text-xs text-[#8e8e93] dark:text-gray-400">{{ directT('modules.members.joiningDate') }}</div>
            <div class="text-sm font-medium text-[#1d1d1f] dark:text-white">{{ viewingMember.joiningDate }}</div>
            </div>
          </div>
          
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5856D6] to-[#AF52DE] flex items-center justify-center shadow-md">
            <i class="material-symbols-outlined text-white !text-[20px]">history</i>
                                </div>
            <div>
            <div class="text-xs text-[#8e8e93] dark:text-gray-400">{{ directT('modules.members.lastActive') }}</div>
            <div class="text-sm font-medium text-[#1d1d1f] dark:text-white">{{ viewingMember.lastActive }}</div>
                                </div>
                            </div>
          
        <!-- Permissions Section -->
        <div class="mt-6 pt-6 border-t border-gray-100 dark:border-white/10">
          <h5 class="text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-3">{{ directT('modules.members.additionalPermissions') }}</h5>
            <div class="space-y-2">
            <div class="flex items-center gap-2" v-if="viewingMember.permissions.is_owner">
              <div class="w-5 h-5 rounded-full bg-[#34C759] flex items-center justify-center">
                <i class="material-symbols-outlined text-white !text-[14px]">check</i>
                </div>
              <div class="text-sm text-[#1d1d1f] dark:text-white">{{ directT('modules.members.organizationOwner') }}</div>
              </div>
              
            <div class="flex items-center gap-2" v-if="viewingMember.permissions.edit_all_leads">
              <div class="w-5 h-5 rounded-full bg-[#34C759] flex items-center justify-center">
                <i class="material-symbols-outlined text-white !text-[14px]">check</i>
                </div>
              <div class="text-sm text-[#1d1d1f] dark:text-white">{{ directT('modules.members.editAllLeads') }}</div>
              </div>
              
            <div class="flex items-center gap-2" v-if="viewingMember.permissions.view_all_leads">
              <div class="w-5 h-5 rounded-full bg-[#34C759] flex items-center justify-center">
                <i class="material-symbols-outlined text-white !text-[14px]">check</i>
                </div>
              <div class="text-sm text-[#1d1d1f] dark:text-white">{{ directT('modules.members.viewAllLeads') }}</div>
              </div>
              
            <div class="flex items-center gap-2" v-if="viewingMember.permissions.can_invite_users">
              <div class="w-5 h-5 rounded-full bg-[#34C759] flex items-center justify-center">
                <i class="material-symbols-outlined text-white !text-[14px]">check</i>
                </div>
              <div class="text-sm text-[#1d1d1f] dark:text-white">{{ directT('modules.members.canInviteUsers') }}</div>
              </div>
              
            <div class="flex items-center gap-2" v-if="viewingMember.permissions.view_all_calendars">
              <div class="w-5 h-5 rounded-full bg-[#34C759] flex items-center justify-center">
                <i class="material-symbols-outlined text-white !text-[14px]">check</i>
                </div>
              <div class="text-sm text-[#1d1d1f] dark:text-white">{{ directT('modules.members.viewAllCalendars') }}</div>
              </div>
              
            <div class="flex items-center gap-2" v-if="!viewingMember.permissions.is_owner && !viewingMember.permissions.edit_all_leads && !viewingMember.permissions.view_all_leads && !viewingMember.permissions.can_invite_users && !viewingMember.permissions.view_all_calendars">
              <div class="text-sm text-[#8e8e93] dark:text-gray-400">{{ directT('modules.members.noAdditionalPermissions') }}</div>
              </div>
            </div>
          </div>
        </div>
        
      <div class="flex items-center justify-end gap-3 mt-8">
          <button 
            @click="showViewModal = false" 
          class="px-6 py-3 rounded-2xl font-medium transition-all bg-[#f2f2f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white hover:bg-[#e5e5ea] dark:hover:bg-white/20"
          >
            {{ directT('modules.members.close') }}
                                </button>
          <button 
            @click="showEditForViewing" 
          class="px-6 py-3 rounded-2xl font-medium transition-all bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {{ directT('modules.members.edit') }}
                                </button>
                                </div>
                                </div>
                            </div>

  <!-- Add Member Modal with modern design -->
  <div v-if="showAddModal" class="fixed inset-0 flex items-center justify-center z-50 px-4">
    <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" @click="cancelAdd"></div>
    <div class="relative transform transition-all duration-300 scale-100 bg-white dark:bg-gray-900/95 backdrop-blur-[20px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto mt-[100px] sm:mt-0">
      
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h3 class="text-2xl font-semibold text-[#1d1d1f] dark:text-white">{{ directT('modules.members.addNewMember') }}</h3>
        <button @click="cancelAdd" class="w-10 h-10 rounded-full bg-[#f2f2f7] dark:bg-white/10 flex items-center justify-center hover:bg-[#e5e5ea] dark:hover:bg-white/20 transition-colors">
          <i class="material-symbols-outlined text-[#3c3c43] dark:text-gray-300">close</i>
                                </button>
                            </div>

      <div v-if="addError" class="mb-4 p-4 bg-[#FFE5E5] dark:bg-red-500/20 rounded-2xl">
        <p class="text-[#FF3B30] font-medium">{{ addError }}</p>
                                </div>

        <form @submit.prevent="addNewMember">
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.name') }}</label>
            <input 
              v-model="newMember.name" 
              type="text" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all" 
              required
            >
                                </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.email') }}</label>
            <input 
              v-model="newMember.email" 
              type="email" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
              required
            >
                            </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.password') }}</label>
            <input 
              v-model="newMember.password" 
              type="password" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
              required
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.phone') }}</label>
            <input 
              v-model="newMember.phone" 
              type="text" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.location') }}</label>
            <input 
              v-model="newMember.location" 
              type="text" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
            >
          </div>
          
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.role') }}</label>
            <select 
              v-model="newMember.role" 
              class="w-full px-4 py-3 bg-white dark:bg-white/5 border border-[#e5e5ea] dark:border-white/10 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-transparent transition-all"
            >
              <option value="admin">{{ directT('modules.members.admin') }}</option>
              <option value="editor">{{ directT('modules.members.editor') }}</option>
              <option value="member">{{ directT('modules.members.member') }}</option>
              <option value="viewer">{{ directT('modules.members.viewer') }}</option>
            </select>
          </div>
          
          <!-- Screen Permissions -->
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.screenPermissions') }}</label>
            
            <div v-if="loadingScreens" class="text-center p-4">
              <div class="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p class="text-[#8e8e93] dark:text-gray-400 mt-2">{{ directT('modules.members.loadingScreens') }}</p>
            </div>
            
            <div v-else-if="screenError" class="p-4 bg-[#FFE5E5] dark:bg-red-500/20 rounded-xl">
              <p class="text-[#FF3B30]">{{ screenError }}</p>
            </div>
            
            <div v-else class="max-h-60 overflow-y-auto border border-[#e5e5ea] dark:border-white/10 rounded-xl p-4 bg-white dark:bg-white/5">
              <div class="grid grid-cols-2 gap-3">
                <div v-for="screen in screens" :key="screen.screen_key" class="flex items-center gap-2 p-2 hover:bg-[#f2f2f7] dark:hover:bg-white/5 rounded-lg transition-colors">
                  <input 
                    type="checkbox" 
                    :id="`new_screen_${screen.screen_key}`" 
                    v-model="newScreenPermissions[screen.screen_key]" 
                    class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                  />
                  <label :for="`new_screen_${screen.screen_key}`" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                    {{ screen.display_name }}
                  </label>
                </div>
              </div>
            </div>
            
            <div class="mt-3 flex justify-between">
              <button 
                type="button"
                @click="selectAllNewScreens(true)"
                class="text-xs text-[#007AFF] hover:underline font-medium"
              >
                {{ directT('modules.members.selectAll') }}
                                </button>
              <button 
                type="button"
                @click="selectAllNewScreens(false)"
                class="text-xs text-[#8e8e93] hover:text-[#3c3c43] dark:text-gray-400 dark:hover:text-gray-300"
              >
                {{ directT('modules.members.deselectAll') }}
                                </button>
                            </div>
                                </div>
          
          <!-- Additional Permissions -->
          <div>
            <label class="block text-sm font-medium text-[#3c3c43] dark:text-gray-300 mb-2">{{ directT('modules.members.additionalPermissions') }}</label>
            
            <div class="space-y-3 border border-[#e5e5ea] dark:border-white/10 rounded-xl p-4 bg-white dark:bg-white/5">
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="new_is_owner" 
                  v-model="newMember.permissions.is_owner" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="new_is_owner" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.organizationOwner') }}
                </label>
                                </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="new_edit_all_leads" 
                  v-model="newMember.permissions.edit_all_leads" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="new_edit_all_leads" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.editAllLeads') }}
                </label>
                            </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="new_view_all_leads" 
                  v-model="newMember.permissions.view_all_leads" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="new_view_all_leads" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.viewAllLeads') }}
                </label>
              </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="new_can_invite_users" 
                  v-model="newMember.permissions.can_invite_users" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="new_can_invite_users" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.canInviteUsers') }}
                </label>
              </div>
              
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="new_view_all_calendars" 
                  v-model="newMember.permissions.view_all_calendars" 
                  class="w-4 h-4 text-[#007AFF] bg-white dark:bg-white/10 border-[#e5e5ea] dark:border-white/20 rounded focus:ring-[#007AFF] focus:ring-2" 
                />
                <label for="new_view_all_calendars" class="cursor-pointer text-sm text-[#1d1d1f] dark:text-white">
                  {{ directT('modules.members.viewAllCalendars') }}
                </label>
              </div>
              </div>
            </div>
          </div>
          
        <div class="flex items-center justify-end gap-3 mt-8">
            <button 
              type="button" 
              @click="cancelAdd" 
            class="px-6 py-3 rounded-2xl font-medium transition-all bg-[#f2f2f7] dark:bg-white/10 text-[#1d1d1f] dark:text-white hover:bg-[#e5e5ea] dark:hover:bg-white/20"
            >
              {{ directT('modules.members.cancel') }}
                                </button>
            <button 
              type="submit" 
            class="px-6 py-3 rounded-2xl font-medium transition-all bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white shadow-md hover:shadow-lg transform hover:scale-105"
              :disabled="addingSaving"
            >
            <span v-if="addingSaving" class="flex items-center gap-2">
              <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              {{ directT('modules.members.adding') }}
            </span>
              <span v-else>{{ directT('modules.members.addMember') }}</span>
                                </button>
                            </div>
        </form>
    </div>
</div>

  <!-- Delete Confirmation Popup -->
  <div v-if="showDeletePopup" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
    <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl border border-[#E5E7EB] dark:border-[#374151] max-w-md w-full mx-4 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] p-6">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
            <i class="material-symbols-outlined text-white text-xl">delete</i>
          </div>
          <div>
            <h3 class="text-xl font-bold text-white">{{ directT('modules.members.deleteConfirmTitle') }}</h3>
            <p class="text-white/80 text-sm">{{ directT('modules.members.deleteConfirmSubtitle') }}</p>
          </div>
        </div>
      </div>
      
      <!-- Content -->
      <div class="p-6">
        <p class="text-[#1a1a1a] dark:text-white text-center mb-6">
          {{ directT('modules.members.deleteConfirmMessage').replace('{memberName}', memberToDelete?.name || '') }}
        </p>
        
        <!-- Actions -->
        <div class="flex gap-3">
          <button 
            @click="cancelDelete"
            class="flex-1 px-4 py-3 bg-[#F3F4F6] dark:bg-[#374151] text-[#1a1a1a] dark:text-white font-medium rounded-2xl transition-all duration-200 hover:bg-[#E5E7EB] dark:hover:bg-[#4B5563]"
          >
            {{ directT('modules.members.cancel') }}
          </button>
          <button 
            @click="confirmDeleteAction"
            class="flex-1 px-4 py-3 bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] text-white font-medium rounded-2xl transition-all duration-200 hover:from-[#E6342A] hover:to-[#E6297D] shadow-lg"
          >
            {{ directT('modules.members.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import Pagination from '@/components/Common/Pagination.vue'
import { getOrganizationMembers, editMember, deleteMember } from '@/services/memberService'
import { supabase } from '@/lib/supabaseClient'
import { Screen, getUserPermissions, getScreens, setScreenPermission } from '@/services/permissionService'
import * as XLSX from 'xlsx'
import { createToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface Member {
  id: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  joiningDate: string;
  lastActive: string;
  role?: string;
  permissions: {
    is_owner: boolean;
    edit_all_leads: boolean;
    view_all_leads: boolean;
    can_invite_users: boolean;
    view_all_calendars: boolean;
  };
}

// Define translation record type
type TranslationRecord = Record<string, any>;
// Define translations object type
type TranslationsType = {
  en: TranslationRecord;
  he: TranslationRecord;
  ar: TranslationRecord;
  [key: string]: TranslationRecord;
};

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Members",
  components: {
    Pagination,
  },
  props: {
    // ניתן להעביר מידע התחלתי, אבל אם לא הועבר - נשלוף את הנתונים מהשרת
    initialMembers: {
      type: Array as () => Member[],
      default: () => []
    },
    // מזהה של ארגון ספציפי לתצוגה
    orgId: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const router = useRouter();
    const { locale } = useI18n();
    const members = ref<Member[]>(props.initialMembers || []);
    const loading = ref(props.initialMembers.length === 0);
    const error = ref<string | null>(null);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const itemsPerPage = 10;
    const showEditModal = ref(false);
    const editingMember = ref<Member>({
      id: '',
      name: '',
      email: '',
      phone: '',
      location: '',
      joiningDate: '',
      lastActive: '',
      role: 'member',
      permissions: {
        is_owner: false,
        edit_all_leads: false,
        view_all_leads: false,
        can_invite_users: false,
        view_all_calendars: false
      }
    });
    const editError = ref<string | null>(null);
    const saving = ref(false);
    const showViewModal = ref(false);
    const viewingMember = ref<Member>({
      id: '',
      name: '',
      email: '',
      phone: '',
      location: '',
      joiningDate: '',
      lastActive: '',
      role: '',
      permissions: {
        is_owner: false,
        edit_all_leads: false,
        view_all_leads: false,
        can_invite_users: false,
        view_all_calendars: false
      }
    });

    // משתנים חדשים להוספת משתמש
    const showAddModal = ref(false);
    const addError = ref<string | null>(null);
    const addingSaving = ref(false);
    const newMember = ref({
      name: '',
      email: '',
      password: '',
      phone: '',
      location: '',
      role: 'member',
      permissions: {
        is_owner: false,
        edit_all_leads: false,
        view_all_leads: false,
        can_invite_users: false,
        view_all_calendars: false
      }
    });
    const newScreenPermissions = ref<Record<string, boolean>>({});
    
    // Delete confirmation popup
    const showDeletePopup = ref(false);
    const memberToDelete = ref<Member | null>(null);

    // משתנים להרשאות מסכים
    const screens = ref<Screen[]>([]);
    const loadingScreens = ref(false);
    const screenError = ref<string | null>(null);
    const screenPermissions = ref<Record<string, boolean>>({});
    
    // Translation files
    const translationFiles: TranslationsType = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Direct translation function that bypasses vue-i18n
    function directT(key: string): string {
      const parts = key.split('.');
      let result: any = translationFiles[locale.value];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: any = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart];
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part];
      }
      return typeof result === 'string' ? result : key;
    }
    
    // פונקציה לטעינת המסכים
    const loadScreens = async () => {
      loadingScreens.value = true;
      screenError.value = null;
      
      try {
        screens.value = await getScreens();
      } catch (err) {
        console.error('Error loading screens:', err);
        screenError.value = 'Failed to load screens. Please try again.';
      } finally {
        loadingScreens.value = false;
      }
    };
    
    // פונקציה להצגת מודל הוספת משתמש
    const showAddMemberModal = async () => {
      // איפוס הטופס
      newMember.value = {
        name: '',
        email: '',
        password: '',
        phone: '',
        location: '',
        role: 'member',
        permissions: {
          is_owner: false,
          edit_all_leads: false,
          view_all_leads: false,
          can_invite_users: false,
          view_all_calendars: false
        }
      };
      
      // איפוס הרשאות מסכים
      newScreenPermissions.value = {};
      addError.value = null;
      showAddModal.value = true;
      
      // טעינת המסכים אם עוד לא נטענו
      if (screens.value.length === 0) {
        await loadScreens();
      }
      
      // אתחול כל ההרשאות ל-false
      screens.value.forEach(screen => {
        newScreenPermissions.value[screen.screen_key] = false;
      });
    };
    
    // פונקציה לביטול הוספת משתמש
    const cancelAdd = () => {
      showAddModal.value = false;
    };
    
    // פונקציה לבחירת כל המסכים או ביטול כולם עבור משתמש חדש
    const selectAllNewScreens = (select: boolean) => {
      screens.value.forEach(screen => {
        newScreenPermissions.value[screen.screen_key] = select;
      });
    };
    
    // פונקציה להוספת משתמש חדש
    const addNewMember = async () => {
      addingSaving.value = true;
      addError.value = null;
      
      try {
        // Get current organization ID
        let orgId: string | null = props.orgId;
        
        if (!orgId) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data: profileData } = await supabase
              .from('user_profiles')
              .select('org_id')
              .eq('user_id', user.id)
              .single();
              
            if (profileData) {
              orgId = profileData.org_id as string;
            }
          }
        }
        
        if (!orgId) {
          throw new Error('Failed to determine organization ID. Cannot create user without organization.');
        }
        
        // First, create the user in Auth
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: newMember.value.email,
          password: newMember.value.password,
        });
        
        if (authError) {
          throw new Error(`Failed to create user account: ${authError.message}`);
        }
        
        if (!authData.user) {
          throw new Error('User account created but user data not returned');
        }
        
        // בדיקה סופית אם יש לנו org_id
        if (!orgId) {
          throw new Error('Failed to determine organization ID. Cannot create user without organization.');
        }
        
        console.log(`Creating user profile with org_id: ${orgId}`);
        
        // Create user profile with permissions
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            user_id: authData.user.id,
            display_name: newMember.value.name,
            email: newMember.value.email,
            phone: newMember.value.phone,
            location: newMember.value.location,
            role: newMember.value.role,
            org_id: orgId as string,
            parent_user_id: (await supabase.auth.getUser()).data.user?.id || null, // Set the parent user to the current user
            created_at: new Date().toISOString(),
            permissions_json: newMember.value.permissions
          })
          .select()
          .single();
        
        if (profileError) {
          throw new Error(`Failed to create user profile: ${profileError.message}`);
        }
        
        // Set screen permissions
        const setPermissionsPromises = screens.value.map(screen => {
          const hasPermission = newScreenPermissions.value[screen.screen_key] || false;
          
          return setScreenPermission(
            authData.user!.id,
            screen.screen_key,
            { 
              can_view: hasPermission, 
              can_edit: hasPermission
            }
          );
        });
        
        await Promise.all(setPermissionsPromises);
        
        // Add to local list
        const currentDate = new Date().toISOString().split('T')[0];
        
        members.value.unshift({
          id: authData.user.id,
          name: newMember.value.name,
          email: newMember.value.email,
          phone: newMember.value.phone,
          location: newMember.value.location,
          joiningDate: currentDate,
          lastActive: currentDate,
          role: newMember.value.role,
          permissions: {
            is_owner: newMember.value.permissions.is_owner,
            edit_all_leads: newMember.value.permissions.edit_all_leads,
            view_all_leads: newMember.value.permissions.view_all_leads,
            can_invite_users: newMember.value.permissions.can_invite_users,
            view_all_calendars: newMember.value.permissions.view_all_calendars
          }
        });
        
        // Close modal
        showAddModal.value = false;
        
        // Show success message
        alert('Member added successfully!');
      } catch (err) {
        console.error('Error adding member:', err);
        addError.value = err instanceof Error ? err.message : 'Failed to add new member. Please try again.';
      } finally {
        addingSaving.value = false;
      }
    };
    
    // פונקציה לטעינת הרשאות המסכים של המשתמש
    const loadUserPermissions = async (userId: string) => {
      loadingScreens.value = true;
      screenError.value = null;
      screenPermissions.value = {};
      
      try {
        // מאפס את כל ההרשאות
        screens.value.forEach(screen => {
          screenPermissions.value[screen.screen_key] = false;
        });
        
        // מקבל את ההרשאות הקיימות
        const permissions = await getUserPermissions(userId);
        
        // מפעיל את הcheckbox עבור כל הרשאה קיימת
        permissions.forEach(permission => {
          if (permission.can_view) {
            screenPermissions.value[permission.screen_id] = true;
          }
        });
      } catch (err) {
        console.error('Error loading user permissions:', err);
        screenError.value = 'Failed to load user permissions. Please try again.';
      } finally {
        loadingScreens.value = false;
      }
    };
    
    // פונקציה להצגת חלון עריכה ולטעינת הרשאות
    const showEditMember = async (member: Member) => {
      // First load the basic member data
      editingMember.value = { ...member };
      showEditModal.value = true;
      editError.value = null;
      
      // טוען את המסכים אם עוד לא נטענו
      if (screens.value.length === 0) {
        await loadScreens();
      }
      
      try {
        // Fetch the latest user profile data to get up-to-date permissions
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('permissions_json')
          .eq('user_id', member.id)
          .single();
        
        console.log('Loaded permissions:', profileData?.permissions_json);
        
        if (profileError) {
          console.error('Error fetching user permissions:', profileError);
        } else if (profileData && profileData.permissions_json) {
          // Update permissions with the latest from the database
          const dbPermissions = profileData.permissions_json as any;
          editingMember.value.permissions = {
            is_owner: dbPermissions.is_owner || false,
            edit_all_leads: dbPermissions.edit_all_leads || false,
            view_all_leads: dbPermissions.view_all_leads || false,
            can_invite_users: dbPermissions.can_invite_users || false,
            view_all_calendars: dbPermissions.view_all_calendars || false
          };
        }
      } catch (err) {
        console.error('Error loading permissions:', err);
      }
      
      // טוען את הרשאות המסכים
      await loadUserPermissions(member.id);
    };
    
    // פונקציה לשמירת השינויים, כולל הרשאות מסכים
    const saveEdit = async () => {
      saving.value = true;
      editError.value = null;
      
      try {
        // שמירת פרטי המשתמש
        await editMember(editingMember.value.id, {
          display_name: editingMember.value.name,
          phone: editingMember.value.phone,
          location: editingMember.value.location,
          role: editingMember.value.role,
          permissions: editingMember.value.permissions
        });
        
        // עדכון הרשאות המסכים
        const updatePermissionsPromises = screens.value.map(screen => {
          const hasPermission = screenPermissions.value[screen.screen_key] || false;
          
          return setScreenPermission(
            editingMember.value.id,
            screen.screen_key,
            { 
              can_view: hasPermission, 
              can_edit: hasPermission
            }
          );
        });
        
        await Promise.all(updatePermissionsPromises);
        
        // עדכון המשתמש ברשימה
        const index = members.value.findIndex(m => m.id === editingMember.value.id);
        if (index !== -1) {
          members.value[index] = {
            ...members.value[index],
            name: editingMember.value.name,
            phone: editingMember.value.phone,
            location: editingMember.value.location,
            role: editingMember.value.role,
            permissions: editingMember.value.permissions
          };
        }
        
        showEditModal.value = false;
      } catch (err) {
        console.error('Error saving member:', err);
        editError.value = 'Failed to save changes. Please try again.';
      } finally {
        saving.value = false;
      }
    };
    
    // פונקציה לביטול העריכה
    const cancelEdit = () => {
      showEditModal.value = false;
      editingMember.value = {
        id: '',
        name: '',
        email: '',
        phone: '',
        location: '',
        joiningDate: '',
        lastActive: '',
        role: 'member',
        permissions: {
          is_owner: false,
          edit_all_leads: false,
          view_all_leads: false,
          can_invite_users: false,
          view_all_calendars: false
        }
      };
      screenPermissions.value = {};
    };
    
    // פונקציה לבחירת כל המסכים או ביטול כולם
    const selectAllScreens = (select: boolean) => {
      screens.value.forEach(screen => {
        screenPermissions.value[screen.screen_key] = select;
      });
    };

    // שליפת הנתונים בעת טעינת הקומפוננט
    onMounted(async () => {
      // בדיקת אימות משתמש
      const { data, error } = await supabase.auth.getSession();
      console.log('Current session:', data);
      
      // אם אין סשן פעיל, הפנה לדף התחברות
      if (!data || !data.session) {
        console.warn('No active session, redirecting to login');
        // להפנות לדף ההתחברות הקיים במערכת
        router.push('/authentication/sign-in');
        return;
      }
      
      // בדיקת קיום טבלת user_profiles וניסיון לאבחן בעיות
      try {
        console.log('Checking user_profiles table...');
        // בדיקה אם הטבלה קיימת - שיטה נכונה
        const { count, error: tablesError } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true });
        
        console.log('Table check result - count:', count, 'error:', tablesError);
        
        if (tablesError) {
          console.error('Error checking user_profiles table:', tablesError);
        } else {
          console.log(`✅ user_profiles table exists with ${count} records`);
        }
        
        // בדיקה אם יש פרופיל למשתמש הנוכחי
        if (data.session?.user?.id) {
          console.log('Checking user profile for user ID:', data.session.user.id);
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', data.session.user.id)
            .maybeSingle();
          
          console.log('User profile check result:', profileData, profileError);
          
          if (profileError) {
            console.error('Error checking user profile:', profileError);
          }
          
          // אם אין פרופיל, ננסה ליצור אחד בסיסי
          if (!profileData) {
            console.log('No profile found, attempting to create one...');
            const { data: newProfile, error: createError } = await supabase
              .from('user_profiles')
              .insert({
                user_id: data.session.user.id,
                display_name: data.session.user.email?.split('@')[0] || 'User',
                email: data.session.user.email,
                role: 'member',
                created_at: new Date().toISOString()
              })
              .select()
              .single();
            
            console.log('Profile creation result:', newProfile, createError);
            
            if (createError) {
              console.error('Error creating user profile:', createError);
            }
          }
        }
      } catch (e) {
        console.error('Debug check failed:', e);
      }
      
      if (members.value.length === 0) {
        await fetchMembers();
      }
    });

    // פונקציה לשליפת החברים מהשרת
    const fetchMembers = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        console.log('Fetching members...');
        const { members: orgMembers, error: apiError } = await getOrganizationMembers(props.orgId);
        
        if (apiError) {
          error.value = apiError;
          console.error('Error fetching members:', apiError);
        } else {
          console.log('Members received:', orgMembers);
          
          // Check if permissions are properly loaded from API
          if (orgMembers.length > 0) {
            console.log('Sample member permissions:', orgMembers[0].permissions);
          }
          
          // Ensure each member has permissions object with default values if not present
          const membersWithPermissions = orgMembers.map(member => {
            const permissions = member.permissions || {};
            return {
              ...member,
              permissions: {
                is_owner: permissions.is_owner || false,
                edit_all_leads: permissions.edit_all_leads || false,
                view_all_leads: permissions.view_all_leads || false,
                can_invite_users: permissions.can_invite_users || false,
                view_all_calendars: (permissions as any).view_all_calendars || false
              }
            };
          });
          
          members.value = membersWithPermissions;
        }
      } catch (err) {
        console.error('Exception in fetchMembers:', err);
        error.value = err instanceof Error ? err.message : 'Failed to fetch members';
      } finally {
        loading.value = false;
      }
    };

    // סינון חברים לפי חיפוש
    const filteredMembers = computed(() => {
      if (!searchQuery.value) return members.value;
      
      const query = searchQuery.value.toLowerCase();
      return members.value.filter(member => 
        member.name.toLowerCase().includes(query) || 
        member.email.toLowerCase().includes(query)
      );
    });

    // חברים בעמוד הנוכחי
    const paginatedMembers = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return filteredMembers.value.slice(start, end);
    });

    // שינוי עמוד
    const changePage = (page: number) => {
      currentPage.value = page;
    };

    // צפייה בחבר
    const viewMember = async (member: Member) => {
      viewingMember.value = { ...member };
      showViewModal.value = true;
      
      try {
        // Fetch the latest user profile data to get up-to-date permissions
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('permissions_json')
          .eq('user_id', member.id)
          .single();
        
        console.log('View permissions data:', profileData?.permissions_json);
        
        if (profileError) {
          console.error('Error fetching user permissions for view:', profileError);
        } else if (profileData && profileData.permissions_json) {
          // Update permissions with the latest from the database
          const dbPermissions = profileData.permissions_json as any;
          viewingMember.value.permissions = {
            is_owner: dbPermissions.is_owner || false,
            edit_all_leads: dbPermissions.edit_all_leads || false,
            view_all_leads: dbPermissions.view_all_leads || false,
            can_invite_users: dbPermissions.can_invite_users || false,
            view_all_calendars: dbPermissions.view_all_calendars || false
          };
        }
      } catch (err) {
        console.error('Error loading permissions for view:', err);
      }
    };

    // אישור מחיקה
    const confirmDelete = (member: Member) => {
      memberToDelete.value = member;
      showDeletePopup.value = true;
    };
    
    const cancelDelete = () => {
      showDeletePopup.value = false;
      memberToDelete.value = null;
    };
    
    const confirmDeleteAction = () => {
      if (memberToDelete.value) {
        handleDeleteMember(memberToDelete.value.id);
        showDeletePopup.value = false;
        memberToDelete.value = null;
      }
    };

    // מחיקת חבר
    const handleDeleteMember = async (memberId: string) => {
      try {
        const { success, error: deleteError } = await deleteMember(memberId);
        
        if (success) {
          members.value = members.value.filter(m => m.id !== memberId);
        } else {
          alert(`Error deleting member: ${deleteError}`);
        }
      } catch (err) {
        alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };

    // איפוס מספר העמוד כאשר משתנה החיפוש
    watch(searchQuery, () => {
      currentPage.value = 1;
    });

    // ייצוא הנתונים ל-Excel
    const exportToExcel = () => {
      // יצירת CSV סטרינג
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // הוספת כותרות העמודות
      csvContent += "ID,Name,Email,Phone,Location,Joining Date,Last Active,Role\n";
      
      // הוספת נתוני החברים
      filteredMembers.value.forEach(member => {
        csvContent += `${member.id},${member.name},"${member.email}","${member.phone}","${member.location}","${member.joiningDate}","${member.lastActive}","${member.role || ''}"\n`;
      });
      
      // יצירת קישור להורדה
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      
      // הורדת הקובץ
      link.click();
      
      // הסרת האלמנט מהעמוד
      document.body.removeChild(link);
    };

    // המרת שם התפקיד לשם מציג
    const getRoleName = (role?: string) => {
      switch (role) {
        case 'admin': return 'Admin';
        case 'editor': return 'Editor';
        case 'member': return 'Member';
        case 'viewer': return 'Viewer';
        default: return 'Undefined';
      }
    };

    // מעבר מצפייה לעריכה
    const showEditForViewing = () => {
      showViewModal.value = false;
      editingMember.value = { ...viewingMember.value };
      showEditModal.value = true;
    };

    return {
      members,
      loading,
      error,
      searchQuery,
      currentPage,
      itemsPerPage,
      filteredMembers,
      paginatedMembers,
      changePage,
      viewMember,
      showEditModal,
      editingMember,
      editError,
      saving,
      showEditMember,
      cancelEdit,
      saveEdit,
      confirmDelete,
      showDeletePopup,
      memberToDelete,
      cancelDelete,
      confirmDeleteAction,
      exportToExcel,
      showViewModal,
      viewingMember,
      getRoleName,
      showEditForViewing,
      
      // משתנים ופונקציות חדשים להרשאות מסכים
      screens,
      loadingScreens,
      screenError,
      screenPermissions,
      selectAllScreens,
      
      // משתנים ופונקציות חדשים להוספת משתמש
      showAddModal,
      addError,
      addingSaving,
      newMember,
      newScreenPermissions,
      showAddMemberModal,
      cancelAdd,
      selectAllNewScreens,
      addNewMember,
      
      // חובה לכלול את directT
      directT
    };
  }
});
</script>
